import React from "react";
import { components } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { asyncPaginateStyles } from "../../../utils/getProductListCardDetails";

/**
 * ExcludableMultiSelect — async multi-select for one filter dimension
 * (brand or a category level) with an optional "select all (except)" mode.
 *
 * UX model:
 * - Every option in the dropdown renders a tick checkbox.
 * - "Выбрать все" is a fixed row at the top of the dropdown. Turning it on
 *   ticks every option; the user then REMOVES ticks to exclude values.
 * - Excluded values appear in the control as red strikethrough chips;
 *   a blue "Все выбраны" chip shows the mode is active (its × turns it off).
 * - Without `onSelectAllChange`, the select-all row is hidden and the control
 *   behaves as a plain include multi-select (still with ticks).
 *
 * The value passed to `onChange` keeps the existing contract of the parent
 * pages: it is the INCLUDE list normally, and the EXCLUDE list while
 * `selectAll` is on.
 */

const Tick = ({ checked }) => (
  <span
    aria-hidden="true"
    className={`tw-flex tw-h-4 tw-w-4 tw-flex-none tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-text-[11px] tw-leading-none tw-transition-colors ${
      checked
        ? "tw-border-blue-500 tw-bg-blue-500 tw-text-white"
        : "tw-border-gray-300 tw-bg-white tw-text-transparent"
    }`}
  >
    ✓
  </span>
);

// Option row with a tick. In select-all mode the tick is INVERTED:
// everything is ticked except the options the user removed (exclusions).
const TickOption = (props) => {
  const { selectAllActive } = props.selectProps;
  const ticked = selectAllActive ? !props.isSelected : props.isSelected;

  return (
    <components.Option {...props}>
      <div className="tw-flex tw-items-center tw-gap-2">
        <Tick checked={ticked} />
        <span className="tw-truncate">{props.label}</span>
      </div>
    </components.Option>
  );
};

// Menu with a fixed "Выбрать все" header ABOVE the scrollable option list,
// so options scroll beneath it instead of over it. Rendered only when the
// consumer opts into select-all by providing a handler.
//
// NOTE: this must be the Menu component, NOT MenuList — react-select-
// async-paginate injects its own scroll-tracking MenuList to trigger
// page loads, and overriding it breaks the infinite scroll.
const SelectAllMenu = (props) => {
  const { selectAllActive, onSelectAllToggle } = props.selectProps;

  return (
    <components.Menu {...props}>
      {typeof onSelectAllToggle === "function" && (
        <div
          role="button"
          className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-t-md tw-border-0 tw-border-b tw-border-solid tw-border-gray-100 tw-bg-white tw-px-3 tw-py-2 hover:tw-bg-blue-50"
          onMouseDown={(e) => {
            // preventDefault keeps the menu open and the input focused
            e.preventDefault();
            e.stopPropagation();
            onSelectAllToggle(!selectAllActive);
          }}
        >
          <Tick checked={!!selectAllActive} />
          <span className="tw-text-sm tw-font-medium tw-text-gray-800">
            Выбрать все
          </span>
        </div>
      )}
      {props.children}
    </components.Menu>
  );
};

// Prepends the "Все выбраны" summary chip while select-all mode is active.
const SelectAllValueContainer = ({ children, ...props }) => {
  const { selectAllActive, onSelectAllToggle } = props.selectProps;

  return (
    <components.ValueContainer {...props}>
      {selectAllActive && (
        <div className="tw-m-[2px] tw-flex tw-items-center tw-gap-1 tw-rounded tw-bg-blue-100 tw-px-2 tw-py-[1px] tw-text-sm tw-text-blue-700">
          <span>Все выбраны</span>
          <span
            title="Отключить режим «выбрать все»"
            className="tw-cursor-pointer tw-px-1 tw-text-blue-400 hover:tw-text-blue-700"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => onSelectAllToggle(false)}
          >
            ×
          </span>
        </div>
      )}
      {children}
    </components.ValueContainer>
  );
};

const selectComponents = {
  Option: TickOption,
  Menu: SelectAllMenu,
  ValueContainer: SelectAllValueContainer,
};

// The tick conveys selection state, so options keep a neutral background;
// in select-all mode the value chips are EXCLUSIONS and are styled red.
const categorySelectStyles = {
  ...asyncPaginateStyles,
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f5f5f5" : "white",
    color: "#000000d9",
    fontSize: "14px",
    padding: "5px 12px",
    "&:active": {
      backgroundColor: "#e6f7ff",
    },
  }),
  placeholder: (provided, state) =>
    state.selectProps.selectAllActive
      ? { ...provided, display: "none" }
      : asyncPaginateStyles.placeholder(provided),
  multiValue: (provided, state) => ({
    ...asyncPaginateStyles.multiValue(provided),
    backgroundColor: state.selectProps.selectAllActive
      ? "#fff1f0"
      : "#f0f0f0",
  }),
  multiValueLabel: (provided, state) => ({
    ...asyncPaginateStyles.multiValueLabel(provided),
    ...(state.selectProps.selectAllActive
      ? { color: "#cf1322", textDecoration: "line-through" }
      : {}),
  }),
};

const ExcludableMultiSelect = ({
  label,
  placeholder,
  value,
  onChange,
  loadOptions,
  selectAll = false,
  onSelectAllChange,
  instanceKey,
}) => (
  <div>
    <label className="form-label mb-2 d-block">{label}</label>
    <AsyncPaginate
      isMulti
      key={instanceKey}
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      placeholder={placeholder}
      isClearable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      debounceTimeout={300}
      additional={{ page: 1 }}
      shouldLoadMore={(scrollHeight, clientHeight, scrollTop) =>
        (scrollTop + clientHeight) / scrollHeight >= 0.6
      }
      styles={categorySelectStyles}
      components={selectComponents}
      selectAllActive={selectAll}
      onSelectAllToggle={onSelectAllChange}
    />
  </div>
);

export default ExcludableMultiSelect;
